import React from 'react'
import { formatCurrency } from '../../lib/utils/formatNumber'
import { CurrencyEnum } from '../../types/Currency'
import { classNames } from '../../lib/utils/classNames'

type EstimatedGainsCardProps = {
  totalProfitability: number
  totalPeriod: number
  locale: string
  livretAGains?: number
  livretARate?: number
  showLivretAComparison?: boolean
  className?: string
}

export default function EstimatedGainsCard({
  totalProfitability,
  totalPeriod,
  locale,
  livretAGains = 0,
  livretARate = 0,
  showLivretAComparison = false,
  className = '',
}: EstimatedGainsCardProps) {
  const safeTotalPeriod = totalPeriod > 0 ? totalPeriod : 1
  const monthlyGain = totalProfitability / (safeTotalPeriod * 12)
  const yearlyGain = totalProfitability / safeTotalPeriod
  const difference = totalProfitability - livretAGains

  const title = locale === 'fr' ? 'Gains estimés :' : 'Estimated gains:'
  const monthLabel = locale === 'fr' ? 'par mois' : 'per month'
  const yearLabel = locale === 'fr' ? 'par an' : 'per year'

  return (
    <div
      className={classNames(
        'rounded-xl bg-purpleGradient p-4 shadow-lg',
        className,
      )}
    >
      <p className="text-center text-sm font-light text-white/90">{title}</p>
      <p className="mb-3 text-center text-3xl font-bold text-white">
        {formatCurrency(totalProfitability, 2, 0, CurrencyEnum.EUR, locale)}
      </p>

      <div className="flex justify-center gap-6 text-white/90">
        <div className="text-center">
          <p className="text-xs font-light">{monthLabel}</p>
          <p className="text-lg font-semibold">
            {formatCurrency(monthlyGain, 2, 0, CurrencyEnum.EUR, locale)}
          </p>
        </div>
        <div className="h-12 w-px bg-white/30" />
        <div className="text-center">
          <p className="text-xs font-light">{yearLabel}</p>
          <p className="text-lg font-semibold">
            {formatCurrency(yearlyGain, 2, 0, CurrencyEnum.EUR, locale)}
          </p>
        </div>
      </div>

      {showLivretAComparison && locale === 'fr' ? (
        <div className="border-t border-white/20 pt-3">
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1 text-white/70">
              <span>Livret A ({livretARate}%)</span>
            </span>
            <div className="text-right">
              <span className="text-white/70">
                {formatCurrency(livretAGains, 2, 0, CurrencyEnum.EUR, locale)}
              </span>
              {difference > 0 && (
                <span className="ml-2 font-semibold text-green-300">
                  +{formatCurrency(difference, 2, 0, CurrencyEnum.EUR, locale)}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
